/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, FC, ReactNode, Dispatch, SetStateAction, useState, useEffect } from 'react';

import { ApiError } from '@libs/types';
import { setData as setFormData, getData as getFormData } from '@libs/utils';
import { getInputErrors, isEmpty } from '@carcareers/utils';

export type FormData = {
  [key: string]: any;
};

type FormDataType<T> = T & FormData;

interface FormContextProps<T> {
  data: FormDataType<T>;
  errors: ApiError[];
  getData: <D = FormData>(path: string) => FormDataType<D>;
  getErrors: (property: string) => string;
  setData: (path: string, value: any) => void;
  setErrors: Dispatch<SetStateAction<ApiError[]>>;
  reset: () => void;
}

interface FormProviderProps<T> {
  initialData?: T;
  children?: ReactNode;
}

const FormContext = createContext<FormContextProps<any>>({} as FormContextProps<any>);

export function useForm<T>() {
  const context = useContext<FormContextProps<T>>(FormContext);

  if (isEmpty(context)) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
}

export const FormProvider: FC<FormProviderProps<any>> = ({ children, initialData }) => {
  const [errors, setErrors] = useState<ApiError[]>([]);
  const [data, setData] = useState();

  const getErrors = (property: string) => getInputErrors(property, errors);

  const getData = (path: string) => getFormData(data, path) ?? null;

  const handleData = (path: string, value: any) => {
    setData((prevData: any) => setFormData(prevData, path, value));
  };

  const reset = () => {
    setData(undefined);
    setErrors([]);
  };

  useEffect(() => {
    if (initialData && !data) {
      setData(structuredClone(initialData));
    }
  }, [initialData, data]);

  return (
    <FormContext.Provider value={{ data, errors, getData, getErrors, setData: handleData, setErrors, reset }}>
      <form style={{ height: '100%', width: '100%' }}>{children}</form>
    </FormContext.Provider>
  );
};
